import { motion } from "framer-motion";
import { ShieldCheck, Globe, TrendingUp, ArrowRightLeft } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { OptimizedPicture } from "@/components/OptimizedPicture";

export const NEASCSection = () => {
  const { translations } = useLanguage();
  const t = translations?.neasc;

  const benefits = [
    {
      key: "quality" as const,
      icon: ShieldCheck,
      color: "from-blue-500/20 to-blue-600/20",
      iconColor: "text-blue-600 dark:text-blue-400"
    },
    {
      key: "recognition" as const,
      icon: Globe,
      color: "from-purple-500/20 to-purple-600/20",
      iconColor: "text-purple-600 dark:text-purple-400"
    },
    {
      key: "improvement" as const,
      icon: TrendingUp,
      color: "from-green-500/20 to-green-600/20",
      iconColor: "text-green-600 dark:text-green-400"
    },
    {
      key: "transferability" as const,
      icon: ArrowRightLeft,
      color: "from-orange-500/20 to-orange-600/20",
      iconColor: "text-orange-600 dark:text-orange-400"
    }
  ];

  if (!t) return null;

  return (
    <section className="py-20 px-4 bg-muted/30">
      <div className="container mx-auto max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Columna izquierda - Logo */}
          <motion.div 
            className="flex flex-col items-center lg:items-start gap-6"
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <OptimizedPicture 
              src="/lovable-uploads/360748c0-43d3-4c73-930f-1365f0ac18f2.png" 
              alt="NEASC Logo" 
              width={256}
              height={128}
              loading="lazy"
              decoding="async"
              className="w-64 h-auto"
            />
            <span className="inline-flex items-center px-4 py-2 rounded-full bg-primary/10 text-primary font-semibold text-sm">
              {t.badge}
            </span>
          </motion.div>
          
          {/* Columna derecha - Contenido */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-foreground">
              {t.title}
            </h2>
            <p className="text-muted-foreground mb-8 text-lg leading-relaxed">
              {t.subtitle}
            </p>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
              {benefits.map((benefit, index) => {
                const Icon = benefit.icon;
                return (
                  <motion.div
                    key={benefit.key}
                    className={`p-5 rounded-lg bg-gradient-to-br ${benefit.color} backdrop-blur-sm border border-border/50 hover:border-border transition-all`}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.4 + index * 0.1 }}
                    whileHover={{ scale: 1.02 }}
                  >
                    <Icon className={`w-8 h-8 mb-3 ${benefit.iconColor}`} />
                    <h3 className="font-semibold text-foreground mb-2">
                      {t.benefits[benefit.key].title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {t.benefits[benefit.key].description}
                    </p>
                  </motion.div>
                );
              })}
            </div>
            
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.8 }}
            >
              <Button 
                asChild
                variant="secondary"
                className="group"
              >
                <a 
                  href={t.learnMoreUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2"
                >
                  {t.learnMore}
                  <ArrowRightLeft className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </a>
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
